import {HoistModel, LoadSpec, managed, XH} from '@xh/hoist/core';
import {action, observable, runInAction} from '@xh/hoist/mobx';
import {OrderTicketModel} from '../orders/OrderTicketModel';
import {FullQuote, Position} from '../../../../data/DataTypes';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';

export class TickerPanelModel extends HoistModel {
    @observable accessor isOpen = false;
    private timer;
    position: Position;
    @observable accessor isListed = false;
    @observable accessor isCash = false;
    @observable accessor name;
    @observable accessor description;
    @observable accessor exchange;
    @observable accessor price = 0;
    @observable accessor priceChange = 0;
    @observable accessor priceChangePct = 0;
    @observable accessor bid = 0;
    @observable accessor bidSize = 0;
    @observable accessor ask = 0;
    @observable accessor askSize = 0;
    @managed orderModel = new OrderTicketModel();

    constructor() {
        super();

        this.addReaction({
            track: () => this.isOpen,
            run: isOpen => {
                if (isOpen) this.loadAsync();
            }
        });
    }

    override onLinked(): void {
        this.position = this.componentProps.position;
    }

    @action open() {
        const {position, getPosition} = this.componentProps;
        this.position = getPosition?.() ?? position;
        this.isOpen = true;
    }

    @action
    close() {
        clearTimeout(this.timer);
        this.isOpen = false;
    }

    buy() {
        this.close();
        this.orderModel.open(this.position.portfolioId, this.position.ticker, 'buy', this.position.quantity);
    }

    sell() {
        this.close();
        this.orderModel.open(this.position.portfolioId, this.position.ticker, 'sell', this.position.quantity);
    }

    sendToChart() {
        this.close();
        // `reload` so that re-sending the ticker already in the route still fires a transition -
        // the chart may have been pointed elsewhere since.
        XH.navigate('default.charts', {ticker: this.position.ticker}, {reload: true});
    }

    sendToResearch() {
        this.close();
        XH.navigate('default.research', {ticker: this.position.ticker});
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        if (!this.isOpen) return;

        const quote: FullQuote = await XH.fetchService
            .getJson({
                url: `${XH.getConf('svcApiUrl')}/quote/fullquote/${this.position.ticker}`,
                ...getDefaultOptions()
            })
            .catchDefault({alertType: 'toast'});

        runInAction(() => {
            this.isListed = quote.isListed;
            this.isCash = quote.isCash;
            this.name = quote.name;
            this.description = quote.description;
            this.exchange = quote.exchange;
            this.price = (quote.bid + quote.ask) / 2;
            this.priceChange = quote.priceChange;
            this.priceChangePct = quote.priceChangePct;
            this.bid = quote.bid;
            this.bidSize = quote.bidSize;
            this.ask = quote.ask;
            this.askSize = quote.askSize;
        });
        this.timer = setTimeout(() => this.loadAsync(), 1000);
    }
}
